import { useNavigate, useParams } from 'react-router-dom';
import { ReactNode, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, IconButton, Switch, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Grid from '@mui/material/Grid2';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import PeopleIcon from '@mui/icons-material/People';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { AffiliateUserSummary } from '../../interfaces/AffiliateUserSummary/AffiliateUserSummary';
import { getAffiliateUser } from '../../api/Affiliate';
import { updateAffiliateProgramForUser } from '../../api/User';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import { DashboardStatsCard } from '../../components/Dashboard/DashboardStatsCard';
import { formatCurrency } from '../../utils/Currency';
import { AffiliateOrdersTable } from './AffiliateOrdersTable';

const SummaryRow = ({
  label,
  value,
  rightSection,
}: {
  label: string;
  value: string;
  rightSection?: ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'row',
      gap: 0.5,
      alignItems: 'center',
    }}
  >
    <Typography sx={{ fontSize: '14px', lineHeight: '16px', fontWeight: 600 }}>
      {label}:
    </Typography>
    <Typography sx={{ fontSize: '14px', lineHeight: '16px' }}>
      {value}
    </Typography>

    {rightSection}
  </Box>
);

const cardSize = { xs: 12, md: 6, xl: 3 };

export const AffiliateUserDetailsPage = () => {
  const navigate = useNavigate();

  const { userID: userIDAsStr } = useParams<string>();

  const userID = userIDAsStr ? Number(userIDAsStr) : undefined;

  const [summary, setSummary] = useState<AffiliateUserSummary | undefined>(
    undefined,
  );
  const [showToggleDialog, setShowToggleDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshSummary = () => {
    if (!Number.isFinite(userID)) return;

    setLoading(true);
    getAffiliateUser(userID!)
      .then((s) => setSummary(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshSummary();
  }, [userID]);

  const handleToggleActive = () => {
    if (!summary) return;

    setShowToggleDialog(false);
    setLoading(true);
    updateAffiliateProgramForUser(
      summary.userID,
      summary.affiliateEnabled ? undefined : summary.affiliateCode,
    )
      .then(() => {
        toast.success(
          `Partner hesabı başarıyla ${
            summary.affiliateEnabled ? 'deaktive' : 'aktive'
          } edildi`,
        );
        refreshSummary();
      })
      .catch((err) => toast.error(err.message));
  };

  const handleCopyAffiliateCode = () => {
    navigator.clipboard
      .writeText(summary?.affiliateCode ?? '')
      .then(() => toast.success('Affiliate kodu panoya kopyalandı'));
  };

  const goBack = () => navigate('/users/affiliate');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <ConfirmDialog
        content={`Gerçekten partner hesabını ${
          summary?.affiliateEnabled ? 'deaktive' : 'aktive'
        } etmek istiyor musunuz?`}
        open={showToggleDialog}
        onClose={() => setShowToggleDialog(false)}
        onConfirm={handleToggleActive}
        color='primary'
      />
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>Partner Hesabı</Typography>
      </Box>

      {!summary && !loading && (
        <Typography>Hesap bilgileri yüklenirken bir hata oluştu</Typography>
      )}

      {!!summary && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.5,
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                {summary.firstName} {summary.lastName}
              </Typography>

              <Switch
                size='small'
                checked={summary.affiliateEnabled}
                onClick={() => setShowToggleDialog(true)}
              />
            </Box>

            <SummaryRow label='E-Posta' value={summary.email} />
            <SummaryRow label='Telefon' value={summary.phone} />
            <SummaryRow
              label='Affiliate Kodu'
              value={summary.affiliateCode}
              rightSection={
                <IconButton
                  color='primary'
                  size='small'
                  onClick={handleCopyAffiliateCode}
                  sx={{ p: 0, width: 'fit-content', height: 'fit-content' }}
                >
                  <ContentCopyIcon sx={{ width: 14, height: 14 }} />
                </IconButton>
              }
            />
          </Box>

          <Grid container spacing={3}>
            <Grid size={cardSize}>
              <DashboardStatsCard
                title='Toplam Ziyaret'
                icon={<AdsClickIcon fontSize='large' color='success' />}
                value={summary.totalClicks.toString()}
              />
            </Grid>

            <Grid size={cardSize}>
              <DashboardStatsCard
                title='Tekil Ziyaretçi'
                icon={<PeopleIcon fontSize='large' sx={{ color: '#f27a8d' }} />}
                value={summary.totalUniqueClicks.toString()}
              />
            </Grid>

            <Grid size={cardSize}>
              <DashboardStatsCard
                title='Toplam Sipariş'
                icon={
                  <ShoppingCartIcon
                    fontSize='large'
                    sx={{ color: '#6589e2' }}
                  />
                }
                value={summary.totalOrders.toString()}
              />
            </Grid>

            <Grid size={cardSize}>
              <DashboardStatsCard
                title='Toplam Gelir'
                icon={
                  <CurrencyExchangeIcon
                    fontSize='large'
                    sx={{ color: '#9499d8' }}
                  />
                }
                value={formatCurrency(summary.totalRevenue)}
              />
            </Grid>
          </Grid>

          <AffiliateOrdersTable affiliateCode={summary.affiliateCode} />
        </Box>
      )}
    </Box>
  );
};
